import type { CancelTokenSource } from 'axios';
import * as con from '../constants';
import { QUERY_STATE } from '../../constants';
import { AnyAction } from 'redux';
import type { DocRetrieveResponse } from '../../openapi/types';
import type { DocsListQuery } from '../../openapi/operations/docsList';

type UploadingAttachment = {
  file: File;
  name: string;
  progress: number;
  cancelToken?: null | CancelTokenSource;
};

const docDetailState = {
  data: {} as DocRetrieveResponse,
  uploadingAttachments: [] as UploadingAttachment[],
  uploadProtocolQueryState: QUERY_STATE.EMPTY,
  loadQueryState: QUERY_STATE.EMPTY,
  removeQueryState: QUERY_STATE.EMPTY,
  uploadQueryState: QUERY_STATE.EMPTY,
  uploadErrors: null,
  docListFilter: {} as DocsListQuery,
};

export const doc = (state = docDetailState, action: AnyAction): typeof docDetailState => {
  switch (action.type) {
    case con.DOCDETAIL_LOAD_SUCCESS:
      return {
        ...state,
        ...{
          data: action.data,
          loadQueryState: QUERY_STATE.SUCCESS,
        },
      };
    case con.DOCDETAIL_LOAD_FAILURE:
      return {
        ...state,
        ...{
          loadQueryState: QUERY_STATE.FAIL,
        },
      };
    case con.DOCDETAIL_LOAD_WAITING:
      return {
        ...state,
        ...{
          data: {} as DocRetrieveResponse,
          loadQueryState: QUERY_STATE.WAITING,
          removeQueryState: QUERY_STATE.EMPTY,
          uploadQueryState: QUERY_STATE.EMPTY,
        },
      };
    case con.DOCDETAIL_REMOVE_WAITING:
      return {
        ...state,
        ...{
          removeQueryState: QUERY_STATE.WAITING,
        },
      };
    case con.DOCDETAIL_REMOVE_SUCCESS:
      return {
        ...state,
        ...{
          removeQueryState: QUERY_STATE.SUCCESS,
        },
      };
    case con.DOCDETAIL_REMOVE_FAILURE:
      return {
        ...state,
        ...{
          removeQueryState: QUERY_STATE.FAIL,
        },
      };
    case con.DOCDETAIL_CHANGE_STATE:
      return {
        ...state,
        ...{
          data: {
            ...state.data,
            state: action.docState,
          },
        },
      };
    case con.DOCDETAIL_UPDATE:
      return {
        ...state,
        ...{
          data: action.data,
        },
      };
    case con.DOCDETAIL_FILES_UPLOAD_WAITING:
      return {
        ...state,
        uploadQueryState: QUERY_STATE.WAITING,
        uploadingAttachments: [
          ...state.uploadingAttachments,
          ...(action.files as File[]).map((file) => ({
            file: file,
            name: file.name,
            progress: 0,
            cancelToken: null,
          })),
        ],
      };
    case con.DOCDETAIL_FILES_UPLOAD_SUCCESS:
      return {
        ...state,
        ...{
          uploadQueryState: QUERY_STATE.SUCCESS,
          data: {
            ...state.data,
            attachments: [...(state.data?.attachments ?? []), action.data],
          },
          uploadingAttachments: state.uploadingAttachments.filter(
            (file) => file && file.file !== action.file,
          ),
        },
      };
    case con.DOCDETAIL_FILES_DELETE_START:
      return {
        ...state,
        ...{
          uploadQueryState: QUERY_STATE.EMPTY,
          data: {
            ...state.data,
            attachments: state.data.attachments.map((attachment) =>
              attachment && attachment.url === action.payload.fileId
                ? {
                    ...attachment,
                    deleteQueryState: action.payload.deleteQueryState,
                  }
                : attachment,
            ),
          },
        },
      };
    case con.DOCDETAIL_FILES_DELETE_SUCCESS:
      return {
        ...state,
        ...{
          data: {
            ...state.data,
            attachments: state.data.attachments.filter(
              (attachment) => attachment && attachment.url !== action.fileId,
            ),
          },
        },
      };
    case con.DOCDETAIL_FILES_SET_UPLOAD_PROGRESS:
      return {
        ...state,
        uploadQueryState: QUERY_STATE.WAITING,
        uploadingAttachments: state.uploadingAttachments.map((attachment) =>
          attachment.file === action.file
            ? {
                ...attachment,
                progress: action.progress,
                cancelToken: action.cancelToken,
              }
            : attachment,
        ),
      };
    case con.DOCDETAIL_FILES_UPLOAD_CANCEL:
      return {
        ...state,
        uploadQueryState: QUERY_STATE.EMPTY,
        uploadingAttachments: state.uploadingAttachments.filter(
          (file) => file && file.cancelToken !== action.cancelToken,
        ),
      };
    case con.DOCDETAIL_FILES_UPLOAD_FAILURE:
      return {
        ...state,
        uploadQueryState: QUERY_STATE.FAIL,
        uploadErrors: action.uploadErrors,
        uploadingAttachments: state.uploadingAttachments.filter(
          (file) => file && file.file !== action.file,
        ),
      };
    case con.DOCDETAIL_FILES_SET_TYPE_SUCCESS:
      return {
        ...state,
        ...{
          data: {
            ...state.data,
            attachments: state.data.attachments.map((attachment) =>
              attachment.url === action.attachment.url ? action.attachment : attachment,
            ),
          },
        },
      };
    case con.DOCDETAIL_SIGNED_PROTOCOL_UPLOAD_WAITING:
      return {
        ...state,
        ...{
          uploadProtocolQueryState: QUERY_STATE.WAITING,
        },
      };
    case con.DOCDETAIL_SIGNED_PROTOCOL_UPLOAD_SUCCESS:
      return {
        ...state,
        ...{
          uploadProtocolQueryState: QUERY_STATE.SUCCESS,
        },
      };
    case con.DOCDETAIL_SIGNED_PROTOCOL_UPLOAD_FAILURE:
      return {
        ...state,
        ...{
          uploadProtocolQueryState: QUERY_STATE.FAIL,
        },
      };
    default:
      return state;
  }
};
